import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { TrashLabel } from './cells/TrashLabel';
import { IInflectTable, IInflectTableCellAugmented } from 'models/InflectsModel';
import React, { ReactNode, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import ModuleContext from '../../../CMFW/context/ModuleContext';
import KeyContext from '../../../CMFW/context/KeyContext';
import BaseInflectsModel, { IInflectBase } from '../../../models/BaseInflectsModel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronRight, faTrash, faListUl } from '@fortawesome/free-solid-svg-icons';
import { useTableHeaders } from './useTableHeaders';
import { TableCellContent } from './TableCellContent';
import { REMOVED_INFLECT, TABLE_COLORS, TABLE_DIMENSIONS, TABLE_TYPOGRAPHY } from './constants';

type IProps = {
  table: IInflectTable;
  cellRenderer: ReactNode;
  showTrash: boolean;
  showAddGroupButton?: boolean;
  onAddGroup?: (inflectIds: number[]) => void;
};

const InflectTable: React.FC<IProps> = (props) => {
  const { table } = props;
  const [inflects, setInflects] = useState(table.inflects);

  const parentKey = useContext(KeyContext);
  const inflectsModel = useContext(ModuleContext) as BaseInflectsModel<IInflectBase>;

  // Use custom hook for headers
  const { columnHeaders, rowHeaders, hasColumnHeaders } = useTableHeaders(table);

  // Consolidate isEmpty logic with useMemo
  const isEmpty = useMemo(
    () => !table.inflects.some((inflect) => inflect.values.some((value) => !inflectsModel.isInflectEmpty(value))),
    [table.inflects, inflectsModel],
  );

  const [isCollapsed, setIsCollapsed] = useState(isEmpty);

  useEffect(() => {
    if (table.inflects) {
      setInflects(table.inflects);
    }
  }, [table.inflects]);

  const updateRemovedInflects = useCallback(
    (inflects: IInflectTableCellAugmented[]) => {
      const inflectIds: number[] = [];
      inflects.forEach((inflect) => inflect.values.forEach((value) => inflectIds.push(value.inflectId)));
      inflectsModel.emptyInflects(inflectIds);
    },
    [inflectsModel],
  );

  const handleTableTrashClick = useCallback(
    (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      event.stopPropagation();

      const updatedInflects = inflects.map((inflect) => ({
        ...inflect,
        values: inflect.values.map((value) => ({ ...value, ...REMOVED_INFLECT })),
      }));

      setInflects(updatedInflects);
      updateRemovedInflects(inflects);
    },
    [inflects, updateRemovedInflects],
  );

  // Generic handler for row/column trash clicks
  const handleLineTrashClick = useCallback(
    (lineId: number, isColumn: boolean) => {
      const lineInflects: IInflectTableCellAugmented[] = [];

      const updatedInflects = inflects.map((inflect) => {
        const matches = isColumn ? inflect.column === lineId : inflect.row === lineId;
        if (matches) {
          lineInflects.push(inflect);
          return {
            ...inflect,
            values: inflect.values.map((value) => ({ ...value, ...REMOVED_INFLECT })),
          };
        }
        return inflect;
      });

      setInflects(updatedInflects);
      updateRemovedInflects(lineInflects);
    },
    [inflects, updateRemovedInflects],
  );

  const handleColumnTrashClick = useCallback(
    (columnId: number) => {
      handleLineTrashClick(columnId, true);
    },
    [handleLineTrashClick],
  );

  const handleRowTrashClick = useCallback(
    (rowId: number) => {
      handleLineTrashClick(rowId, false);
    },
    [handleLineTrashClick],
  );

  // Create cell lookup map for O(1) access
  const cellLookup = useMemo(() => {
    const lookup = new Map<string, IInflectTableCellAugmented>();
    inflects.forEach((cell) => {
      lookup.set(`${cell.row}-${cell.column}`, cell);
    });
    return lookup;
  }, [inflects]);

  return (
    <TableWrapper>
      <TableHeader>
        <HeaderClickArea onClick={() => setIsCollapsed(!isCollapsed)}>
          <ChevronIcon>
            <FontAwesomeIcon icon={isCollapsed ? faChevronRight : faChevronDown} />
          </ChevronIcon>
          <TableTitle>{table.name}</TableTitle>
        </HeaderClickArea>
        {!isEmpty && props.showTrash && (
          <TrashIconWrapper onClick={(e) => e.stopPropagation()}>
            <FontAwesomeIcon icon={faTrash} onClick={handleTableTrashClick} />
          </TrashIconWrapper>
        )}
      </TableHeader>
      {!isCollapsed && (
        <TableContainer>
          <TableStyled size="small">
            <TableHead>
              <TableRow>
                {columnHeaders.map((header) => (
                  <Header key={header.id} component="th">
                    <TrashLabel
                      onlyHover
                      handleTrashClick={() => handleColumnTrashClick(header.id)}
                      label={header.name}
                      showTrash={props.showTrash}
                    />
                  </Header>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rowHeaders.map((rowHeader, rowIndex) => (
                <StyledTableRow key={rowHeader.id} $isEven={rowIndex % 2 === 0}>
                  <RowHeader key={rowHeader.id + '-' + rowHeader.name} component="th" scope="row">
                    <RowHeaderContent>
                      <TrashLabel
                        onlyHover
                        handleTrashClick={() => handleRowTrashClick(rowHeader.id)}
                        label={rowHeader.name}
                        showTrash={props.showTrash}
                      />
                      {props.showAddGroupButton && (
                        <AddGroupButton
                          onClick={() => {
                            // Get all inflect IDs for this row
                            const rowInflectIds = inflects
                              .filter((inflect) => inflect.row === rowHeader.id)
                              .map((inflect) => inflect.id);
                            if (props.onAddGroup) {
                              props.onAddGroup(rowInflectIds);
                            }
                          }}
                          title="Add new group for this row"
                        >
                          <FontAwesomeIcon icon={faListUl} />
                        </AddGroupButton>
                      )}
                    </RowHeaderContent>
                  </RowHeader>
                  {hasColumnHeaders
                    ? columnHeaders.slice(1).map((columnHeader) => {
                        const cell = cellLookup.get(`${rowHeader.id}-${columnHeader.id}`);
                        return (
                          <TableCellStyled key={`${rowHeader.id}-${columnHeader.id}`} component="td" scope="row">
                            <TableCellContent cell={cell} parentKey={parentKey} cellRenderer={props.cellRenderer} />
                          </TableCellStyled>
                        );
                      })
                    : // When there are no column headers, render cells directly
                      inflects
                        .filter((inflect) => inflect.row === rowHeader.id)
                        .map((cell) => (
                          <TableCellStyled key={cell.id} component="td" scope="row">
                            <TableCellContent cell={cell} parentKey={parentKey} cellRenderer={props.cellRenderer} />
                          </TableCellStyled>
                        ))}
                </StyledTableRow>
              ))}
            </TableBody>
          </TableStyled>
        </TableContainer>
      )}
    </TableWrapper>
  );
};

const TableWrapper = styled.div`
  background-color: ${TABLE_COLORS.cellBg};
  border-radius: 8px;
  border: 1px solid ${TABLE_COLORS.borderColorDark};
  overflow: hidden;
  display: inline-block;
`;

const TableHeader = styled.div`
  background: ${TABLE_COLORS.headerBg};
  padding: ${TABLE_DIMENSIONS.headerPadding};
  display: flex;
  align-items: center;
  justify-content: space-between;
  user-select: none;
  transition: background 0.2s ease;
  min-width: 0;

  &:hover {
    background: ${TABLE_COLORS.headerHoverBg};
  }
`;

const HeaderClickArea = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  flex: 1;
  min-width: 0;
`;

const ChevronIcon = styled.div`
  color: ${TABLE_COLORS.chevronIcon};
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  flex-shrink: 0;
  width: ${TABLE_DIMENSIONS.iconSize}px;
  height: ${TABLE_DIMENSIONS.iconSize}px;
`;

const TrashIconWrapper = styled.div`
  color: ${TABLE_COLORS.trashIcon};
  padding: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  border-radius: 4px;
  flex-shrink: 0;
  margin-left: 16px;

  &:hover {
    color: ${TABLE_COLORS.trashIconHover};
    background-color: ${TABLE_COLORS.trashIconHoverBg};
  }
`;

const RowHeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const AddGroupButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  color: #9e9e9e;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #f5f5f5;
    color: #757575;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const TableStyled = styled(Table)`
  && {
    width: auto;
    font-family: ${TABLE_TYPOGRAPHY.fontFamily};
  }
`;

const TableCellStyled = styled(TableCell)`
  && {
    width: ${TABLE_DIMENSIONS.cellWidth}px;
    padding: ${TABLE_DIMENSIONS.cellPadding};
    vertical-align: middle;
    border: 1px solid ${TABLE_COLORS.borderColor};
    border-right: none;
    border-bottom: none;
    transition: background-color 0.15s ease;
    min-height: ${TABLE_DIMENSIONS.cellMinHeight}px;

    &:hover {
      background-color: ${TABLE_COLORS.cellHoverBg};
    }

    &:last-child {
      border-right: 1px solid ${TABLE_COLORS.borderColor};
    }
  }
`;

const Header = styled(TableCellStyled)`
  && {
    background-color: ${TABLE_COLORS.cellHoverBg};
    color: ${TABLE_COLORS.textPrimary};
    border: 1px solid ${TABLE_COLORS.borderColor};
    font-weight: ${TABLE_TYPOGRAPHY.cellFontWeight};
    font-size: ${TABLE_TYPOGRAPHY.cellFontSize};
    letter-spacing: ${TABLE_TYPOGRAPHY.letterSpacing};

    &:first-child {
      font-weight: ${TABLE_TYPOGRAPHY.rowHeaderFontWeight};
      text-align: left;
      padding-left: 20px;
      position: sticky;
      left: 0;
      background-color: ${TABLE_COLORS.cellHoverBg};
    }

    &:not(:first-child) {
      text-align: center;
    }
  }
`;

const TableTitle = styled.div`
  font-weight: ${TABLE_TYPOGRAPHY.headerFontWeight};
  font-size: ${TABLE_TYPOGRAPHY.headerFontSize};
  color: ${TABLE_COLORS.headerText};
  letter-spacing: -${TABLE_TYPOGRAPHY.letterSpacing};
  line-height: 1.5;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &::before {
    content: '';
    display: inline-block;
    width: ${TABLE_DIMENSIONS.accentBarWidth}px;
    height: ${TABLE_DIMENSIONS.accentBarHeight}px;
    background-color: ${TABLE_COLORS.accentGreen};
    margin-right: 12px;
    border-radius: 2px;
    flex-shrink: 0;
  }
`;

const StyledTableRow = styled(TableRow)<{ $isEven: boolean }>`
  && {
    background-color: ${(props) => (props.$isEven ? TABLE_COLORS.cellBg : TABLE_COLORS.cellAltBg)};

    &:hover {
      background-color: ${TABLE_COLORS.cellHoverBg};
    }

    &:last-child td {
      border-bottom: 1px solid ${TABLE_COLORS.borderColor};
    }
  }
`;

const RowHeader = styled(TableCellStyled)`
  && {
    background-color: ${TABLE_COLORS.cellHoverBg};
    color: ${TABLE_COLORS.textPrimary};
    border: 1px solid ${TABLE_COLORS.borderColor};
    font-weight: ${TABLE_TYPOGRAPHY.rowHeaderFontWeight};
    font-size: ${TABLE_TYPOGRAPHY.cellFontSize};
    letter-spacing: ${TABLE_TYPOGRAPHY.letterSpacing};
    text-align: left;
    padding-left: 20px;
  }
`;

export default InflectTable;
